import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import AsesoriaIcon from "../../static/media/home/Asesoria.webp";
import ProvicionIcon from "../../static/media/home/Provision.webp";
import LogisticaIcon from "../../static/media/home/Logistica.webp";
import MaterialesIcon from "../../static/media/home/Materiales.webp";

const C = {
  dark: "#0C1F2F",
  navy: "#0057A8",
  gray: "#F5F7FA",
  teal: "#5BC8AF",
};

const VALUE_PROPS = [
  {
    id: 1,
    title: "Asesoría Técnica",
    description:
      "Te guiamos en la selección exacta de componentes para optimizar tu presupuesto y la seguridad de tu proyecto.",
    img: AsesoriaIcon,
  },
  {
    id: 2,
    title: "Provisión Integral",
    description:
      "Catálogo completo: cables, iluminación, automatización y ferretería en un solo aliado estratégico.",
    img: ProvicionIcon,
  },
  {
    id: 3,
    title: "Logística Eficiente",
    description:
      "Garantizamos despachos rápidos a nivel nacional y entregas directas en tu punto de obra.",
    img: LogisticaIcon,
  },
  {
    id: 4,
    title: "Calidad Certificada",
    description:
      "Trabajamos exclusivamente con marcas reconocidas que cumplen normativas vigentes, con garantías reales.",
    img: MaterialesIcon,
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

// Componente Tarjeta Interno para reusar en Grid y Swiper
const ValueCard = ({ prop }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: { xs: "center", md: "flex-start" },
      textAlign: { xs: "center", md: "left" },
      background: { xs: C.white, md: "transparent" },
      borderRadius: { xs: "20px", md: "0px" },
      p: { xs: 3, md: 0 },
      boxShadow: { xs: "0 10px 30px rgba(0,0,0,0.05)", md: "none" },
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: { xs: 60, md: 56 },
        height: { xs: 60, md: 56 },
        mb: 3,
        display: "flex",
        justifyContent: { xs: "center", md: "flex-start" },
      }}
    >
      <Box
        component="img"
        src={prop.img}
        alt={prop.title}
        sx={{ width: "100%", height: "100%", objectFit: "contain" }}
      />
    </Box>
    <Typography
      sx={{
        color: C.dark,
        fontWeight: 700,
        fontSize: { xs: 18, lg: 20 },
        mb: 1.5,
        lineHeight: 1.2,
      }}
    >
      {prop.title}
    </Typography>
    <Typography
      sx={{
        color: "#5c6c7a",
        fontSize: { xs: 14, lg: 15 },
        lineHeight: 1.6,
        fontWeight: 500,
      }}
    >
      {prop.description}
    </Typography>
  </Box>
);

export default function ValueProposition() {
  return (
    <Box
      id="propuesta-de-valor"
      sx={{
        background: C.gray,
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        pt: { xs: "80px", md: "120px" },
        pb: { xs: "25vw", md: "12vw" },
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: "1280px",
          mx: "auto",
          px: { xs: "20px", sm: "40px", md: "56px" },
          boxSizing: "border-box",
          position: "relative",
          zIndex: 1,
          width: "100%",
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          sx={{ mb: { xs: 5, md: 10 }, textAlign: "center" }}
        >
          <Typography
            variant="h2"
            sx={{
              color: C.dark,
              fontWeight: 750,
              fontSize: { xs: 28, sm: 34, md: 40 },
              lineHeight: 1.15,
              letterSpacing: "-.02em",
              mb: 2,
            }}
          >
            ¿Por qué elegirnos?
          </Typography>
          <Box
            sx={{
              width: "60px",
              height: "3px",
              background: C.navy,
              mx: "auto",
              borderRadius: "2px",
            }}
          />
        </Box>

        {/* VERSIÓN DESKTOP: CUADRÍCULA */}
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          sx={{
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "repeat(4, 1fr)",
            columnGap: 4,
          }}
        >
          {VALUE_PROPS.map((prop, index) => (
            <Box
              key={prop.id}
              component={motion.div}
              variants={itemVariants}
              sx={{
                borderRight:
                  index !== VALUE_PROPS.length - 1
                    ? "1px solid rgba(12, 31, 47, 0.08)"
                    : "none",
                pr: index !== VALUE_PROPS.length - 1 ? 4 : 0,
                pl: index !== 0 ? 4 : 0,
              }}
            >
              <ValueCard prop={prop} />
            </Box>
          ))}
        </Box>

        {/* VERSIÓN MOBILE: SWIPER (CARRUSEL AUTOMÁTICO) */}
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            mx: "-10px",
            px: "10px",
            pb: 2,
          }}
        >
          <Swiper
            modules={[Autoplay]}
            loop={true}
            spaceBetween={16}
            breakpoints={{
              0: { slidesPerView: 1.2 }, // Muestra 1 tarjeta entera y un poco de la segunda para invitar a deslizar
              450: { slidesPerView: 2 }, // En móviles más grandes (apaisado) muestra 2
            }}
            autoplay={{ delay: 3500, disableOnInteraction: false }}
            style={{ padding: "10px" }}
          >
            {VALUE_PROPS.map((prop) => (
              <SwiperSlide key={prop.id}>
                <ValueCard prop={prop} />
              </SwiperSlide>
            ))}
          </Swiper>
        </Box>
      </Box>

      {/* OLA INFERIOR */}
      <Box
        sx={{
          position: "absolute",
          bottom: -1,
          left: 0,
          width: "100%",
          overflow: "hidden",
          lineHeight: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <svg
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          style={{
            display: "block",
            width: "100%",
            height: "10vw",
            minHeight: "80px",
            transform: "scaleX(-1)",
          }}
        >
          <path
            fill={C.navy}
            d="M0,192L80,181.3C160,171,320,149,480,160C640,171,800,213,960,208C1120,203,1280,149,1360,122.7L1440,96L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
          ></path>
        </svg>
      </Box>
    </Box>
  );
}
